import { useEffect, useMemo, useState, type CSSProperties, type FormEvent } from 'react';
import { Link, Navigate } from 'react-router-dom';
import type {
  TenantDetail,
  TenantListQuery,
  TenantStatus,
  TenantSummary,
  UpdateTenantInput,
} from '@neuroedge/shared';
import {
  DataGrid,
  dayBoundary,
  useGridState,
  type GridColumn,
  type GridState,
} from '../../components/data-grid/DataGrid';
import { Button, Card, PageState, TextField, useToast } from '../../components/ui';
import { ApiClientError } from '../../api/client';
import { fieldErrorsFromApiError } from '../../lib/field-errors';
import { useAuth } from '../../auth/AuthContext';
import {
  useTenants,
  useTenantCounts,
  useTenant,
  useUpdateTenant,
  useTenantMembers,
  useTenantAudit,
  useTenantMenuAccess,
  useSetTenantMenuAccess,
  useResetTenantMenuAccess,
} from '../../api/admin';
import {
  usePlans,
  useTenantSubscription,
  useAssignSubscription,
  useExtendTrial,
  useTenantInvoices,
  useGenerateInvoice,
  useVoidInvoice,
  useMarkInvoicePaid,
  useDownloadInvoiceDocument,
} from '../../api/billing';
import { TenantSuspensionPanel } from './CollectionsCards';
import { TenantLimitsPanel } from '../billing/LimitsCards';

const STATUSES: TenantStatus[] = ['ACTIVE', 'TRIAL', 'SUSPENDED', 'CLOSED'];
type Tab = 'profile' | 'dashboard' | 'menuAccess' | 'features' | 'members' | 'activity' | 'billing';
const TABS: { id: Tab; label: string }[] = [
  { id: 'profile', label: 'Profile' },
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'menuAccess', label: 'Menu Access' },
  { id: 'features', label: 'Features' },
  { id: 'members', label: 'Members' },
  { id: 'activity', label: 'Activity' },
  { id: 'billing', label: 'Billing' },
];

const muted: CSSProperties = { color: 'var(--color-text-muted)' };

type ProfileForm = {
  name: string;
  legalName: string;
  industry: string;
  timezone: string;
  locale: string;
  contactEmail: string;
  contactPhone: string;
};

function formFrom(t: TenantDetail): ProfileForm {
  return {
    name: t.name,
    legalName: t.legalName ?? '',
    industry: t.industry ?? '',
    timezone: t.timezone,
    locale: t.locale,
    contactEmail: t.contactEmail ?? '',
    contactPhone: t.contactPhone ?? '',
  };
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Mirrors the backend's `updateTenantBody` limits (admin.schemas.ts). `legalName`,
 * `industry` and `contactPhone` are nullable/clearable fields — an empty string is
 * how the UI clears them and is NOT flagged as invalid, only a value that is too
 * long. `name`, `timezone` and `locale` are required-ish (min length enforced even
 * when the field is present in the patch), so an empty value there IS an error.
 * `contactEmail` accepts either a valid email or an empty string (clears it).
 */
function validate(form: ProfileForm): Record<string, string> {
  const errors: Record<string, string> = {};
  const name = form.name.trim();
  if (name.length < 1) errors.name = 'Enter a name.';
  else if (name.length > 120) errors.name = 'Name must be 120 characters or fewer.';

  if (form.legalName.trim().length > 200) {
    errors.legalName = 'Legal name must be 200 characters or fewer.';
  }
  if (form.industry.trim().length > 120) {
    errors.industry = 'Industry must be 120 characters or fewer.';
  }

  const timezone = form.timezone.trim();
  if (timezone.length < 1) errors.timezone = 'Enter a timezone.';
  else if (timezone.length > 64) errors.timezone = 'Timezone must be 64 characters or fewer.';

  const locale = form.locale.trim();
  if (locale.length < 1) errors.locale = 'Enter a locale.';
  else if (locale.length < 2) errors.locale = 'Locale must be at least 2 characters.';
  else if (locale.length > 16) errors.locale = 'Locale must be 16 characters or fewer.';

  const contactEmail = form.contactEmail.trim();
  if (contactEmail.length > 0) {
    if (contactEmail.length > 254 || !EMAIL_RE.test(contactEmail)) {
      errors.contactEmail = 'Enter a valid email address.';
    }
  }
  if (form.contactPhone.trim().length > 40) {
    errors.contactPhone = 'Contact phone must be 40 characters or fewer.';
  }

  return errors;
}

function ProfileTab({ tenant }: { tenant: TenantDetail }) {
  const update = useUpdateTenant();
  const toast = useToast();
  const [form, setForm] = useState<ProfileForm>(() => formFrom(tenant));
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setForm(formFrom(tenant));
  }, [tenant]);

  const original = useMemo(() => formFrom(tenant), [tenant]);
  const changed = useMemo(
    () => (Object.keys(form) as (keyof ProfileForm)[]).filter((k) => form[k] !== original[k]),
    [form, original],
  );

  const set = (k: keyof ProfileForm) => (e: { target: { value: string } }) => {
    setSaved(false);
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setFieldErrors((f) => {
      if (!f[k]) return f;
      const { [k]: _removed, ...rest } = f;
      return rest;
    });
  };

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (changed.length === 0) return;

    const errors = validate(form);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError('Please fix the highlighted fields.');
      toast.error('Please fix the highlighted fields.');
      return;
    }

    const patch: UpdateTenantInput = {};
    for (const k of changed) patch[k] = form[k];
    try {
      await update.mutateAsync({ id: tenant.id, patch });
      setSaved(true);
      toast.success('Tenant updated.');
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'VALIDATION_FAILED') {
        const serverFieldErrors = fieldErrorsFromApiError(err);
        if (Object.keys(serverFieldErrors).length > 0) {
          setFieldErrors(serverFieldErrors);
          setError('Please fix the highlighted fields.');
          toast.error('Please fix the highlighted fields.');
        } else {
          setError(err.message);
          toast.error(err.message);
        }
      } else {
        const message = err instanceof Error ? err.message : 'Could not save the tenant.';
        setError(message);
        toast.error(message);
      }
    }
  }

  const field = (k: keyof ProfileForm, label: string, opts: { required?: boolean } = {}) => (
    <TextField
      label={label}
      required={opts.required}
      value={form[k]}
      onChange={set(k)}
      error={fieldErrors[k]}
    />
  );

  return (
    <form
      onSubmit={onSubmit}
      style={{ display: 'grid', gap: 'var(--space-3)', maxWidth: 460 }}
      noValidate
    >
      {field('name', 'Name', { required: true })}
      {field('legalName', 'Legal name')}
      {field('industry', 'Industry')}
      {field('timezone', 'Timezone', { required: true })}
      {field('locale', 'Locale', { required: true })}
      {field('contactEmail', 'Contact email')}
      {field('contactPhone', 'Contact phone')}
      {error ? <p style={{ margin: 0, color: 'var(--color-danger)' }}>{error}</p> : null}
      {saved ? <p style={{ margin: 0, color: 'var(--color-success, green)' }}>Saved.</p> : null}
      <div>
        <Button type="submit" disabled={update.isPending || changed.length === 0}>
          {update.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </form>
  );
}

function DashboardTab({ tenant }: { tenant: TenantDetail }) {
  const c = tenant.counts;
  const ai = tenant.aiImageUsage;
  const aiTotal = ai.success + ai.failed;
  const aiSuccessRate = aiTotal > 0 ? Math.round((ai.success / aiTotal) * 1000) / 10 : null;
  const rows: [string, number | string][] = [
    ['Members', c.members],
    ['Channels', c.channels],
    ['Contacts', c.contacts],
    ['Leads', c.leads],
    ['Conversations', c.conversations],
    ['Messages', c.messages],
    ['AI quotations generated', c.quotations],
    [
      'AI images processed',
      aiTotal === 0 ? '0' : `${ai.success} succeeded, ${ai.failed} failed (${aiSuccessRate}%)`,
    ],
    ['Appointments', c.appointments],
    ['Follow-ups', c.followUps],
  ];
  return (
    <dl
      style={{
        margin: 0,
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        gap: 'var(--space-2) var(--space-4)',
      }}
    >
      {rows.map(([k, v]) => (
        <div key={k} style={{ display: 'contents' }}>
          <dt style={muted}>{k}</dt>
          <dd style={{ margin: 0 }}>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function MenuAccessTab({ tenantId }: { tenantId: string }) {
  const catalog = useTenantMenuAccess(tenantId);
  const setAccess = useSetTenantMenuAccess();
  const reset = useResetTenantMenuAccess();
  const toast = useToast();
  const [pending, setPending] = useState<Set<string> | null>(null);

  async function resetToPlan() {
    try {
      await reset.mutateAsync(tenantId);
      setPending(null);
      toast.success('Menus reset to the plan.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not reset the menus.');
    }
  }

  if (catalog.isPending) return <PageState kind="loading" message="Loading…" />;
  if (catalog.isError) return <PageState kind="error" message={catalog.error.message} />;

  const selected = pending ?? new Set(catalog.data.filter((m) => m.granted).map((m) => m.key));
  const groups = [...new Set(catalog.data.map((m) => m.groupLabel))];
  const dirty = pending !== null;

  function toggle(key: string) {
    setPending((prev) => {
      const next = new Set(prev ?? selected);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function save() {
    if (!pending) return;
    try {
      await setAccess.mutateAsync({ tenantId, menuKeys: [...pending] });
      setPending(null);
      toast.success('Menu access updated.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update menu access.');
    }
  }

  const hasOverrides = catalog.data.some((m) => m.override);

  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
      <p style={{ margin: 0, ...muted }}>
        Menus come from the workspace&apos;s plan. Tick or untick a menu to switch it on or off for
        this workspace only - that stays when the plan changes. Plan menus are set in Pricing plans.
      </p>
      {groups.map((group) => (
        <fieldset key={group} style={{ border: 'none', margin: 0, padding: 0 }}>
          <legend style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, ...muted }}>
            {group}
          </legend>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
            {catalog.data
              .filter((m) => m.groupLabel === group)
              .map((m) => (
                <label
                  key={m.key}
                  style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(m.key)}
                    onChange={() => toggle(m.key)}
                  />
                  {m.label}
                  {m.override === 'GRANT' ? (
                    <span className="ne-badge" style={{ color: 'var(--color-success)' }}>
                      added by admin
                    </span>
                  ) : m.override === 'REVOKE' ? (
                    <span className="ne-badge" style={{ color: 'var(--color-danger)' }}>
                      removed by admin
                    </span>
                  ) : m.inPlan ? (
                    <span className="ne-badge">in plan</span>
                  ) : null}
                </label>
              ))}
          </div>
        </fieldset>
      ))}
      <div>
        <Button disabled={!dirty || setAccess.isPending} onClick={() => void save()}>
          {setAccess.isPending ? 'Saving…' : 'Save menu access'}
        </Button>
        {hasOverrides && !dirty ? (
          <Button
            variant="secondary"
            style={{ marginLeft: 'var(--space-2)' }}
            disabled={reset.isPending}
            onClick={() => void resetToPlan()}
          >
            Reset to plan
          </Button>
        ) : null}
        {dirty ? (
          <Button
            variant="secondary"
            style={{ marginLeft: 'var(--space-2)' }}
            onClick={() => setPending(null)}
          >
            Discard changes
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function FeaturesTab({ tenant }: { tenant: TenantDetail }) {
  const update = useUpdateTenant();
  const toast = useToast();

  async function toggleAiQuotation(checked: boolean) {
    try {
      await update.mutateAsync({ id: tenant.id, patch: { aiQuotationEnabled: checked } });
      toast.success(checked ? 'AI quotations enabled.' : 'AI quotations disabled.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update this feature.');
    }
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <input
          type="checkbox"
          checked={tenant.aiQuotationEnabled}
          disabled={update.isPending}
          onChange={(e) => void toggleAiQuotation(e.target.checked)}
        />
        AI-assisted quotations (photo/PDF extraction)
      </label>
      <p style={{ margin: 0, ...muted, fontSize: 'var(--font-size-sm)' }}>
        Platform-admin rollout flag - enable only for tenants who have asked for it.
      </p>
    </div>
  );
}

function MembersTab({ tenantId }: { tenantId: string }) {
  const members = useTenantMembers(tenantId);
  if (members.isPending) return <PageState kind="loading" message="Loading members…" />;
  if (members.isError) return <PageState kind="error" message={members.error.message} />;
  if (members.data.length === 0) return <PageState kind="empty" title="No members" />;
  return (
    <ul
      style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 'var(--space-2)' }}
    >
      {members.data.map((m) => (
        <li
          key={m.tenantUserId}
          style={{ borderBottom: '1px solid var(--color-border)', paddingBottom: 'var(--space-2)' }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-2)' }}>
            <strong>{m.displayName}</strong>
            <span className="ne-badge">{m.status}</span>
          </div>
          <div style={{ ...muted, fontSize: 'var(--font-size-sm)' }}>
            {m.email}
            {m.isPlatformAdmin ? ' · platform admin' : ''}
            {m.roles.length ? ` · ${m.roles.join(', ')}` : ''}
          </div>
        </li>
      ))}
    </ul>
  );
}

function ActivityTab({ tenantId }: { tenantId: string }) {
  const audit = useTenantAudit(tenantId);
  if (audit.isPending) return <PageState kind="loading" message="Loading activity…" />;
  if (audit.isError) return <PageState kind="error" message={audit.error.message} />;
  if (audit.data.length === 0) return <PageState kind="empty" title="No recorded activity" />;
  return (
    <ul
      style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 'var(--space-2)' }}
    >
      {audit.data.map((a) => (
        <li
          key={a.id}
          style={{ borderBottom: '1px solid var(--color-border)', paddingBottom: 'var(--space-2)' }}
        >
          <div>
            <code>{a.action}</code>{' '}
            <span style={muted}>
              {Object.keys(a.metadata).length ? JSON.stringify(a.metadata) : ''}
            </span>
          </div>
          <div style={{ ...muted, fontSize: 'var(--font-size-sm)' }}>
            {a.actorName ?? a.actorEmail ?? 'system'} · {new Date(a.createdAt).toLocaleString()}
          </div>
        </li>
      ))}
    </ul>
  );
}

function money(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function firstOfNextMonthIso(): string {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth() + 1, 1).toISOString().slice(0, 10);
}

function BillingTab({ tenantId }: { tenantId: string }) {
  const plans = usePlans();
  const subscription = useTenantSubscription(tenantId);
  const assign = useAssignSubscription(tenantId);
  const extendTrial = useExtendTrial(tenantId);
  const invoices = useTenantInvoices(tenantId);
  const generate = useGenerateInvoice(tenantId);
  const voidInvoice = useVoidInvoice(tenantId);
  const markPaid = useMarkInvoicePaid(tenantId);
  const download = useDownloadInvoiceDocument(tenantId);

  async function onDownload(invoiceId: string, kind: 'pdf' | 'receipt') {
    try {
      await download.mutateAsync({ invoiceId, kind });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not download the document.');
    }
  }
  const toast = useToast();

  const [planId, setPlanId] = useState('');
  const [trialEndsAt, setTrialEndsAt] = useState('');
  const [periodStart, setPeriodStart] = useState(todayIso());
  const [periodEnd, setPeriodEnd] = useState(firstOfNextMonthIso());
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const activePlans = (plans.data ?? []).filter((p) => p.status === 'ACTIVE');

  function applyApiError(err: unknown, fallback: string): void {
    if (err instanceof ApiClientError && err.code === 'VALIDATION_FAILED') {
      const serverFieldErrors = fieldErrorsFromApiError(err);
      if (Object.keys(serverFieldErrors).length > 0) {
        setFieldErrors(serverFieldErrors);
        setError('Please fix the highlighted fields.');
        toast.error('Please fix the highlighted fields.');
        return;
      }
    }
    const message = err instanceof Error ? err.message : fallback;
    setError(message);
    toast.error(message);
  }

  async function onAssign() {
    if (!planId) return;
    setError(null);
    setFieldErrors({});
    try {
      await assign.mutateAsync({ planId });
      toast.success('Subscription assigned.');
    } catch (err) {
      applyApiError(err, 'Could not assign the plan.');
    }
  }

  async function onExtendTrial() {
    if (!trialEndsAt) return;
    setError(null);
    setFieldErrors({});
    try {
      await extendTrial.mutateAsync({ endsAt: new Date(trialEndsAt).toISOString() });
      setTrialEndsAt('');
      toast.success('Trial end date updated.');
    } catch (err) {
      applyApiError(err, 'Could not update the trial end date.');
    }
  }

  async function onGenerate() {
    setError(null);
    const errors: Record<string, string> = {};
    if (!periodStart) errors.periodStart = 'Choose a start date.';
    if (!periodEnd) errors.periodEnd = 'Choose an end date.';
    else if (periodStart && periodEnd <= periodStart) {
      errors.periodEnd = 'Must be after the period start.';
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError('Please fix the highlighted fields.');
      toast.error('Please fix the highlighted fields.');
      return;
    }

    try {
      await generate.mutateAsync({ periodStart, periodEnd });
      toast.success('Invoice generated.');
    } catch (err) {
      applyApiError(err, 'Could not generate the invoice.');
    }
  }

  async function onMarkPaid(invoiceId: string) {
    const reference = window.prompt(
      'Payment reference (e.g. UTR / UPI ref / cheque no.) for this bank or offline payment:',
    );
    if (!reference || reference.trim().length < 2) return;
    try {
      await markPaid.mutateAsync({ invoiceId, reference: reference.trim() });
      toast.success('Invoice marked as paid.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not mark the invoice as paid.');
    }
  }

  async function onVoid(invoiceId: string) {
    if (!window.confirm('Void this invoice? This cannot be undone.')) return;
    try {
      await voidInvoice.mutateAsync(invoiceId);
      toast.success('Invoice voided.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not void the invoice.');
    }
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <TenantSuspensionPanel tenantId={tenantId} />
      <TenantLimitsPanel tenantId={tenantId} />
      <div>
        <h3 style={{ margin: '0 0 var(--space-2)', fontSize: 'var(--font-size-base)' }}>
          Subscription
        </h3>
        {subscription.isPending ? (
          <PageState kind="loading" message="Loading subscription…" />
        ) : subscription.data ? (
          <p style={{ margin: 0 }}>
            <strong>{subscription.data.planName}</strong>{' '}
            <span className="ne-badge">{subscription.data.status}</span> —{' '}
            {money(subscription.data.monthlyFeeAmount, subscription.data.currency)}/mo
            {subscription.data.endsAt ? (
              <>
                {' '}
                · {subscription.data.status === 'TRIALING' ? 'trial ends' : 'ends'}{' '}
                {new Date(subscription.data.endsAt).toLocaleDateString()}
              </>
            ) : null}
          </p>
        ) : (
          <p style={{ ...muted, margin: 0 }}>No subscription yet.</p>
        )}
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-2)',
            alignItems: 'center',
            marginTop: 'var(--space-2)',
            flexWrap: 'wrap',
          }}
        >
          <select
            className="ne-input"
            style={{ width: 'auto' }}
            aria-label="Plan to assign"
            value={planId}
            onChange={(e) => setPlanId(e.target.value)}
          >
            <option value="">Choose a plan…</option>
            {activePlans.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({money(p.monthlyFeeAmount, p.currency)}/mo)
              </option>
            ))}
          </select>
          <Button
            type="button"
            variant="secondary"
            disabled={!planId || assign.isPending}
            onClick={() => void onAssign()}
          >
            {assign.isPending ? 'Assigning…' : 'Assign plan'}
          </Button>
        </div>
        {subscription.data &&
        (subscription.data.status === 'TRIALING' || subscription.data.status === 'ACTIVE') ? (
          <div
            style={{
              display: 'flex',
              gap: 'var(--space-2)',
              alignItems: 'center',
              marginTop: 'var(--space-2)',
              flexWrap: 'wrap',
            }}
          >
            <TextField
              label="New trial/subscription end date"
              type="date"
              value={trialEndsAt}
              onChange={(e) => setTrialEndsAt(e.target.value)}
            />
            <Button
              type="button"
              variant="secondary"
              disabled={!trialEndsAt || extendTrial.isPending}
              onClick={() => void onExtendTrial()}
            >
              {extendTrial.isPending ? 'Updating…' : 'Update end date'}
            </Button>
          </div>
        ) : null}
      </div>

      <div>
        <h3 style={{ margin: '0 0 var(--space-2)', fontSize: 'var(--font-size-base)' }}>
          Invoices
        </h3>
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-2)',
            alignItems: 'center',
            marginBottom: 'var(--space-3)',
            flexWrap: 'wrap',
          }}
        >
          <TextField
            label="Period start"
            type="date"
            value={periodStart}
            onChange={(e) => {
              setPeriodStart(e.target.value);
              setFieldErrors((f) => ({ ...f, periodStart: '' }));
            }}
            error={fieldErrors.periodStart}
          />
          <TextField
            label="Period end"
            type="date"
            value={periodEnd}
            onChange={(e) => {
              setPeriodEnd(e.target.value);
              setFieldErrors((f) => ({ ...f, periodEnd: '' }));
            }}
            error={fieldErrors.periodEnd}
          />
          <Button
            type="button"
            variant="secondary"
            disabled={generate.isPending}
            onClick={() => void onGenerate()}
          >
            {generate.isPending ? 'Generating…' : 'Generate invoice'}
          </Button>
        </div>
        {error ? (
          <p style={{ margin: '0 0 var(--space-2)', color: 'var(--color-danger)' }}>{error}</p>
        ) : null}

        {invoices.isPending ? (
          <PageState kind="loading" message="Loading invoices…" />
        ) : (invoices.data?.items.length ?? 0) === 0 ? (
          <PageState kind="empty" title="No invoices yet" />
        ) : (
          <ul
            style={{
              margin: 0,
              padding: 0,
              listStyle: 'none',
              display: 'grid',
              gap: 'var(--space-2)',
            }}
          >
            {invoices.data!.items.map((inv) => (
              <li
                key={inv.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  borderBottom: '1px solid var(--color-border)',
                  paddingBottom: 'var(--space-2)',
                }}
              >
                <span>
                  <strong>{inv.invoiceNumber ?? '—'}</strong> · {inv.periodStart} → {inv.periodEnd}{' '}
                  · {money(inv.totalAmount, inv.currency)}
                  {inv.taxAmount > 0 ? (
                    <span style={muted}> (incl. GST {money(inv.taxAmount, inv.currency)})</span>
                  ) : null}{' '}
                  <span className="ne-badge">{inv.status}</span>
                  {inv.paidReference ? <span style={muted}> · {inv.paidReference}</span> : null}
                </span>
                <span style={{ display: 'flex', gap: 'var(--space-1)', flexWrap: 'wrap' }}>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={download.isPending}
                    onClick={() => void onDownload(inv.id, 'pdf')}
                  >
                    Invoice
                  </Button>
                  {inv.status === 'PAID' ? (
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={download.isPending}
                      onClick={() => void onDownload(inv.id, 'receipt')}
                    >
                      Receipt
                    </Button>
                  ) : null}
                </span>
                {inv.status === 'ISSUED' ? (
                  <span style={{ display: 'flex', gap: 'var(--space-1)' }}>
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={markPaid.isPending}
                      onClick={() => void onMarkPaid(inv.id)}
                    >
                      Mark paid
                    </Button>
                    <Button type="button" variant="secondary" onClick={() => void onVoid(inv.id)}>
                      Void
                    </Button>
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function TenantDetailPanel({ tenantId }: { tenantId: string }) {
  const detail = useTenant(tenantId);
  const update = useUpdateTenant();
  const toast = useToast();
  const [tab, setTab] = useState<Tab>('profile');
  const [statusError, setStatusError] = useState<string | null>(null);

  if (detail.isPending) return <PageState kind="loading" message="Loading tenant…" />;
  if (detail.isError)
    return (
      <PageState
        kind="error"
        message={detail.error.message}
        onRetry={() => void detail.refetch()}
      />
    );

  const t = detail.data;

  return (
    <Card title={t.name}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 'var(--space-2)',
          marginBottom: 'var(--space-3)',
        }}
      >
        <span style={{ ...muted, fontSize: 'var(--font-size-sm)' }}>{t.slug}</span>
        <select
          className="ne-input"
          aria-label="Tenant status"
          style={{ width: 'auto' }}
          value={t.status}
          disabled={update.isPending}
          onChange={(e) => {
            setStatusError(null);
            update
              .mutateAsync({ id: t.id, patch: { status: e.target.value as TenantStatus } })
              .then(() => toast.success('Tenant status updated.'))
              .catch((err) => {
                const message = err instanceof Error ? err.message : 'Could not update the status.';
                setStatusError(message);
                toast.error(message);
              });
          }}
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>
      {statusError ? (
        <p style={{ margin: '0 0 var(--space-2)', color: 'var(--color-danger)' }}>{statusError}</p>
      ) : null}

      <div
        style={{
          display: 'flex',
          gap: 'var(--space-2)',
          marginBottom: 'var(--space-3)',
          flexWrap: 'wrap',
        }}
      >
        {TABS.map((x) => (
          <Button
            key={x.id}
            type="button"
            variant={tab === x.id ? 'primary' : 'secondary'}
            onClick={() => setTab(x.id)}
          >
            {x.label}
          </Button>
        ))}
      </div>

      {tab === 'profile' ? <ProfileTab tenant={t} /> : null}
      {tab === 'dashboard' ? <DashboardTab tenant={t} /> : null}
      {tab === 'menuAccess' ? <MenuAccessTab tenantId={t.id} /> : null}
      {tab === 'features' ? <FeaturesTab tenant={t} /> : null}
      {tab === 'members' ? <MembersTab tenantId={t.id} /> : null}
      {tab === 'activity' ? <ActivityTab tenantId={t.id} /> : null}
      {tab === 'billing' ? <BillingTab tenantId={t.id} /> : null}
    </Card>
  );
}

type StatusTab = 'ALL' | TenantStatus;

function toTenantQuery(tab: StatusTab, g: GridState): TenantListQuery {
  const f = g.filters;
  return {
    page: g.page,
    pageSize: g.pageSize,
    sortBy: g.sortBy as TenantListQuery['sortBy'],
    sortOrder: g.sortOrder,
    status: tab === 'ALL' ? undefined : tab,
    search: f.name?.trim() || undefined,
    email: f.contactEmail?.trim() || undefined,
    industry: f.industry?.trim() || undefined,
    createdFrom: dayBoundary(f.createdAtFrom, false),
    createdTo: dayBoundary(f.createdAtTo, true),
  };
}

/**
 * Tenant Management: every tenant on the standard grid (status tabs with
 * counts, filters for name/slug, email, industry and created date,
 * sorting, paging). "Manage" opens that tenant's tabs (profile, dashboard,
 * menu access, features, members, activity, billing) below the grid.
 */
export function TenantsPage() {
  const { isPlatformAdmin } = useAuth();
  const [tab, setTab] = useState<StatusTab>('ALL');
  const [grid, setGrid] = useGridState({ sortBy: 'createdAt', sortOrder: 'desc' });
  const [selected, setSelected] = useState<string | undefined>(undefined);
  const query = toTenantQuery(tab, grid);
  const tenants = useTenants(query);
  const counts = useTenantCounts(query);

  if (!isPlatformAdmin) return <Navigate to="/" replace />;

  const columns: GridColumn<TenantSummary>[] = [
    {
      key: 'name',
      header: 'Tenant',
      sortable: true,
      filter: { type: 'text', placeholder: 'Name or slug' },
      render: (t) => (
        <>
          <strong>{t.name}</strong>
          <div style={{ ...muted, fontSize: 'var(--font-size-sm)' }}>{t.slug}</div>
        </>
      ),
    },
    {
      key: 'contactEmail',
      header: 'Contact email',
      filter: { type: 'text' },
      render: (t) => t.contactEmail ?? '—',
    },
    {
      key: 'industry',
      header: 'Industry',
      sortable: true,
      filter: { type: 'text' },
      render: (t) => t.industry ?? '—',
    },
    {
      key: 'memberCount',
      header: 'Users',
      sortable: true,
      align: 'right',
      render: (t) => t.memberCount,
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (t) => <span className="ne-badge">{t.status}</span>,
    },
    {
      key: 'createdAt',
      header: 'Created',
      sortable: true,
      filter: { type: 'dateRange' },
      render: (t) => new Date(t.createdAt).toLocaleDateString(),
    },
    {
      key: 'actions',
      header: '',
      render: (t) => (
        <Button
          variant={t.id === selected ? 'primary' : 'secondary'}
          aria-pressed={t.id === selected}
          aria-label={`Manage ${t.name}`}
          onClick={() => setSelected(t.id === selected ? undefined : t.id)}
        >
          {t.id === selected ? 'Managing' : 'Manage'}
        </Button>
      ),
    },
  ];

  const statusTabs: { id: StatusTab; label: string; count: number | undefined }[] = [
    { id: 'ALL', label: 'All', count: counts.data?.all },
    ...STATUSES.map((s) => ({
      id: s as StatusTab,
      label: s.charAt(0) + s.slice(1).toLowerCase(),
      count: counts.data?.[s],
    })),
  ];

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>Tenant Management</h1>

      <Card>
        <div role="tablist" aria-label="Tenants by status" className="ne-tabs">
          {statusTabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className="ne-tabs__tab"
              onClick={() => {
                setTab(t.id);
                setGrid({ ...grid, page: 1 });
              }}
            >
              {t.label}
              {t.count !== undefined ? <span className="ne-tabs__count">{t.count}</span> : null}
            </button>
          ))}
        </div>
        <div style={{ marginTop: 'var(--space-3)' }}>
          {tenants.isError ? (
            <PageState
              kind="error"
              message={tenants.error.message}
              onRetry={() => void tenants.refetch()}
            />
          ) : (
            <DataGrid
              columns={columns}
              rows={tenants.data?.items ?? []}
              rowKey={(t) => t.id}
              rowLabel={(t) => t.name}
              total={tenants.data?.meta.total ?? 0}
              state={grid}
              onStateChange={setGrid}
              loading={tenants.isFetching}
              emptyMessage="No tenants match these filters."
            />
          )}
        </div>
      </Card>

      {selected ? <TenantDetailPanel tenantId={selected} /> : null}

      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        <Link to="/">← Back to dashboard</Link>
      </p>
    </div>
  );
}
